import React, { useEffect, useId, useRef } from 'react';

/** Distribute focal points for radial gradients across the icon shape. */
const focalPoints = [
  { cx: '25%', cy: '75%' },
  { cx: '75%', cy: '25%' },
  { cx: '25%', cy: '25%' },
  { cx: '75%', cy: '75%' },
  { cx: '50%', cy: '50%' },
] as const;

const DRIFT_DUR = '3s';

/** Return a random percentage between 15% and 85%. */
const randPct = () => `${15 + Math.floor(Math.random() * 70)}%`;

export const DynamicInvoiceIcon = ({
  colors,
  height = 18,
  hoverAnimate = false,
  alwaysAnimate = false,
}: {
  colors: string[];
  height?: number;
  hoverAnimate?: boolean;
  /** When true, the gradient animation loops continuously. */
  alwaysAnimate?: boolean;
}) => {
  const id = useId();
  const svgRef = useRef<SVGSVGElement>(null);
  const fallback = '#D9D9D9';
  const fills = colors.length > 0 ? colors : [fallback];
  const width = height * (18 / 24);
  const animate = hoverAnimate || alwaysAnimate;

  useEffect(() => {
    if (!animate || !svgRef.current) return undefined;
    const svg = svgRef.current;

    const triggerAnimation = () => {
      svg.querySelectorAll('animate').forEach((anim) => {
        const start = anim.getAttribute('data-start') ?? '50%';
        const mid = randPct();
        anim.setAttribute('values', `${start};${mid};${start}`);
        // The following eslint disable exists 'cause jsdom does not implement
        // the SVGAnimateElement interface, so beginElement is undefined.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        anim.beginElement?.();
      });
    };

    if (alwaysAnimate) {
      triggerAnimation();
      const interval = setInterval(triggerAnimation, 3000);
      return () => clearInterval(interval);
    }

    const parent = svg.closest('.group');
    if (!parent) return undefined;

    parent.addEventListener('mouseenter', triggerAnimation);
    return () => {
      parent.removeEventListener('mouseenter', triggerAnimation);
    };
  }, [animate, alwaysAnimate]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      viewBox="0 0 18 24"
      fill="none"
      className="shrink-0"
    >
      <defs>
        {/* eslint-disable react/no-array-index-key */}
        {fills.map((color, i) => {
          const fp = focalPoints[i % focalPoints.length];
          return (
            <radialGradient
              key={`${id}-rg-${i}`}
              id={`${id}-rg-${i}`}
              cx={fp.cx}
              cy={fp.cy}
              r="70%"
            >
              <stop offset="0%" stopColor={color} stopOpacity={1} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
              {animate && (
                <>
                  <animate
                    attributeName="cx"
                    data-start={fp.cx}
                    values={`${fp.cx};${fp.cx};${fp.cx}`}
                    dur={DRIFT_DUR}
                    begin="indefinite"
                    fill="freeze"
                  />
                  <animate
                    attributeName="cy"
                    data-start={fp.cy}
                    values={`${fp.cy};${fp.cy};${fp.cy}`}
                    dur={DRIFT_DUR}
                    begin="indefinite"
                    fill="freeze"
                  />
                </>
              )}
            </radialGradient>
          );
        })}
        <mask
          id={`${id}-mask`}
          style={{ maskType: 'alpha' }}
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="18"
          height="24"
        >
          <rect width="18" height="24" fill="#D9D9D9" />
        </mask>
      </defs>
      <g mask={`url(#${id}-mask)`}>
        {/* Base: first color as solid fill */}
        <path
          d="M11.5 6C11.5 6.27614 11.7239 6.5 12 6.5H18V23.5C18 23.7761 17.7761 24 17.5 24H0.5C0.223858 24 8.05326e-09 23.7761 0 23.5V0.5C0 0.223858 0.223858 8.05324e-09 0.5 0H11.5V6Z"
          fill={fills[0]}
        />
        {/* Overlay each additional color as a radial gradient */}
        {fills.map((_, i) =>
          i === 0 ? null : (
            <path
              key={`${id}-p-${i}`}
              d="M11.5 6C11.5 6.27614 11.7239 6.5 12 6.5H18V23.5C18 23.7761 17.7761 24 17.5 24H0.5C0.223858 24 8.05326e-09 23.7761 0 23.5V0.5C0 0.223858 0.223858 8.05324e-09 0.5 0H11.5V6Z"
              fill={`url(#${id}-rg-${i})`}
            />
          )
        )}
        {/* eslint-enable react/no-array-index-key */}
        {/* Folded corner */}
        <path
          d="M12.25 0.301777V5.625C12.25 5.69404 12.306 5.75 12.375 5.75L17.6982 5.75C17.8096 5.75 17.8654 5.61536 17.7866 5.53661L12.4634 0.213388C12.3846 0.134643 12.25 0.190413 12.25 0.301777Z"
          fill={fills.length > 1 ? fills[1] : fills[0]}
          opacity={0.8}
        />
      </g>
    </svg>
  );
};
